import { useMutation } from '@apollo/client';
import type { SafetyAction, UpsertActionInput } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_ACTION, GET_COMMUNITY_ACTIONS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpsertCommunityActionResponse {
  upsertCommunityAction: SafetyAction;
}

export function useUpsertCommunityAction(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [upsertActionMutation, { loading }] = useMutation<UpsertCommunityActionResponse>(UPSERT_COMMUNITY_ACTION, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_ACTIONS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertAction = async (communityId: string, actionName: string, input: UpsertActionInput): Promise<boolean> => {
    try {
      track('UpsertCommunityActionInitiated', {
        communityId,
        actionName,
      });

      await upsertActionMutation({
        variables: {
          communityId,
          actionName,
          input,
        },
      });

      track('UpsertCommunityActionSucceeded', {
        communityId,
        actionName,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityActionFailed', {
        communityId,
        actionName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertAction,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityAction;
